'use client';

import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { useLocale } from './use-locale';
import { localizedHref } from '@/lib/i18n/href';

// Wrapper around next/navigation's useRouter that auto-prepends the current
// locale to push/replace/prefetch targets. Mirrors the useRouter API — just
// drop the `use` change and the rest of the call site stays the same.
// Exposed as useRouter too so existing call sites can just swap the import
// path and keep `const router = useRouter()` unchanged.
export { useLocaleRouter as useRouter };

export function useLocaleRouter() {
    const router = useRouter();
    const locale = useLocale();

    return useMemo(() => ({
        push: (href: string, options?: Parameters<typeof router.push>[1]) =>
            router.push(localizedHref(href, locale), options),
        replace: (href: string, options?: Parameters<typeof router.replace>[1]) =>
            router.replace(localizedHref(href, locale), options),
        prefetch: (href: string, options?: Parameters<typeof router.prefetch>[1]) =>
            router.prefetch(localizedHref(href, locale), options),
        back: router.back,
        forward: router.forward,
        refresh: router.refresh,
    }), [router, locale]);
}
